// App du partenaire (D-037), d'après la maquette « Partenaire · son espace » :
//   Accueil : sa visibilité du mois, les appels du club (« Je participe »), ses emplacements ;
//   Visibilité : le détail, et demander la bannière d'accueil ou un « match présenté par » ;
//   Publier : son offre réservée aux membres, validée par le bureau ;
//   Club : la page du club, son agenda public, le contact, le don.
// Tout passe par le bureau : le partenaire demande, le club valide.
// L'app offerte par un partenaire (D-147) : proposée d'office tant qu'aucun partenaire ne l'offre, la promesse de don.
// Partenaire aussi bénévole (D-071) : sa casquette Bénévole (D-097), annoncée par une ligne à l'accueil.
// Une seule offre (D-103) : celle qu'il publie, avec sa fréquence, est son avantage dans l'app du club.
// Avantages validés par QR code (D-156, maquettes b6, b7, d1) : « Valider un avantage » en tête de l'accueil (scanner avec
// l'appareil photo, ou « Taper un code ») ; Visibilité : ses validations (l'heure, la façon, qui chez lui, le code ; jamais
// le nom du membre).
import { renderMyReceiptsBlock } from './recus';
import {
  askPlacement, myRequests, partnerAnswerAsk, partnerAsks, partnerOpenMatches, partnerReport, publicAgenda,
  type OpenMatch, type Partner, type PartnerAsk, type PartnerReport, type PartnerRequest, type PublicEvent,
} from '../lib/api';
import { clicks, KIND_LABEL, PLACEMENT_LABEL, views, type Placement } from '../domain/partners';
import { EVENT_KIND_LABEL } from '../domain/club-events';
import { localDate } from '../domain/schedule';
import { clubPerks, type Perk } from '../lib/api';
import { endedLine } from '../domain/perk-stock';
import { partnerValidations, type PartnerValidations } from '../lib/perk-qr-api';
import { openCodeSheet, partnerValidationsBlock, validateCard } from './perk-scan';
import { firstName, type Ctx } from './context';
import { time } from './format';
import { $, $$, html, mount, toast, type Html } from './dom';
import { all, cardRow, clubBlock, fixedRow, homeFoot, spot, startHome, todoCard, todoPageHead, type Slot } from './home-kit';
import { OFFER_FREQS, offerLine, partnerMark } from './partners';
import { FREQ_LABEL } from '../domain/pictos';
import type { OfferFrequency } from '../lib/api';
import { helpFoot } from './help';
import { auClub, loadOffer, offerCard, wireOffer } from './official-partner';
import { euros } from '../domain/licences';
import type { AppOffer } from '../lib/lien-api';
import { renderVolunteerLine } from './volunteer';
import { renderFirstSteps } from '../guide/first-steps';
import { guideMark } from '../lib/guide';
import { renderReferralCard } from './apporteurs';
import { renderPartnerLienCard } from './lien';

const fmt = (iso: string, o: Intl.DateTimeFormatOptions) => new Intl.DateTimeFormat('fr-FR', o).format(new Date(iso));
/** Le jour puis l'heure au format de l'app : « dim. 20 déc. · 9h00 » (jamais « 09:00 »). */
const fmtAt = (iso: string, o: Intl.DateTimeFormatOptions) => `${fmt(iso, o)} · ${time(new Date(iso))}`;
const STATUS = { propose: 'À valider', accepte: 'Validé', refuse: 'Refusé' } as const;

async function load(ctx: Ctx): Promise<{ p: Partner; r: PartnerReport; asks: PartnerAsk[]; reqs: PartnerRequest[]; official: AppOffer | null } | null> {
  const p = ctx.partners[0];
  if (!p) return null;
  const [r, asks, reqs, official] = await Promise.all([
    partnerReport(p.report_token), partnerAsks(p.report_token).catch(() => [] as PartnerAsk[]), myRequests(p.id).catch(() => [] as PartnerRequest[]),
    loadOffer(p.report_token),
  ]);
  return r ? { p, r, asks, reqs, official } : null;
}
const missing = html`<div class="empty"><p class="empty-title">Espace partenaire indisponible.</p></div>`;

const askCard = (a: PartnerAsk): Html => html`
  <section class="pr-ask">
    <p class="pub-kicker">Le club a besoin de vous · ${fmt(a.starts_at, { day: 'numeric', month: 'short' })}</p>
    <h2>${a.event} : ${a.label}</h2>
    ${a.counterpart ? html`<p>En échange : ${a.counterpart}</p>` : ''}
    ${a.mine ? html`<p class="pr-ask-state">${a.status === 'accepte' ? 'Merci : votre participation est confirmée.' : 'Proposé : le club vous recontacte pour confirmer.'}</p>`
      : html`<button type="button" class="btn pr-ask-btn" data-guide="pa-ask" data-ask="${a.id}">Je participe</button>`}
  </section>`;

function wireAsks(root: HTMLElement, p: Partner, again: () => void) {
  for (const b of $$<HTMLButtonElement>(root, '[data-ask]')) {
    b.addEventListener('click', async () => {
      b.disabled = true;
      try { await partnerAnswerAsk(p.report_token, b.dataset.ask!); toast('Merci ! Le club est prévenu'); again(); }
      catch { b.disabled = false; toast('Cet appel n’est plus disponible.', 'ko'); }
    });
  }
}

/** L'avantage de mon offre (D-131) : le nombre limité, épuisée ou non (le partenaire lit ses avantages, jamais qui). */
async function myOfferPerk(p: Partner): Promise<Perk | null> {
  return (await clubPerks(p.club_id)).find(k => k.partner_id === p.id && k.active) ?? null;
}
/** « Les 2 sont partis le 24 octobre » (sans « Terminé : »). */
const outLine = (k: Perk) => { const t = endedLine(k.stock ?? 0, k.exhausted_at!).replace(/^Terminé : /, '').replace(/\.$/, ''); return t.charAt(0).toUpperCase() + t.slice(1); };

/** Accueil du partenaire, recomposé par D-155 (vouvoiement) : son identité dans l'en-tête ; en vedette, ce que le club lui
 *  demande (offrir l'app, sinon un appel du club) ; « Valider un avantage » (D-156) ; « Le club vous attend » (l'invitation,
 *  les autres appels) ; sa visibilité en grands chiffres (ceux du mois, ou de la saison le 1er du mois, quand tout est à 0) ;
 *  ses emplacements ; « Et aussi » (apporter une entreprise, le don, le bénévolat). */
export async function renderPartnerHome(root: HTMLElement, ctx: Ctx, opts: { page?: boolean } = {}): Promise<void> {
  const d = await load(ctx).catch(() => null);
  if (!d) { mount(root, missing); return; }
  const { p, r, asks, reqs, official } = d;
  const today = localDate(new Date());
  const page = !!opts.page;
  const bannerOn = !!r.partner.banner_until && r.partner.banner_until >= today;
  const officialOn = official?.state === 'mine';
  const nextMatch = r.matches.find(m => m.starts_at >= new Date().toISOString());
  const pendingOffer = reqs.find(x => x.kind === 'offer' && x.status === 'propose');
  const pendingBanner = reqs.find(x => x.kind === 'banner' && x.status === 'propose');
  // D-131 : l'offre limitée, épuisée (le club peut la remettre en jeu).
  const offerPerk = r.partner.member_offer ? await myOfferPerk(p).catch(() => null) : null;
  const offerOut = !!offerPerk?.exhausted_at && offerPerk.stock != null;
  const live = [r.partner.show_public, bannerOn, !!nextMatch, !!r.partner.member_offer && !offerOut, officialOn].filter(Boolean).length;
  const name = firstName(ctx);
  // La vedette : le premier appel du club qui attend sa réponse ; sans appel, offrir l'app (D-147, tant qu'aucun partenaire
  // ne l'offre), qui sinon est une ligne de « Le club vous attend ».
  const openAsks = asks.filter(a => !a.mine);
  const offerable = !!official && official.state !== 'mine';
  const lead = openAsks[0] ?? null;
  const rows: Html[] = [
    ...(offerable && lead ? [fixedRow(30, { ico: 'ti-handshake', guide: 'pa-offer', t: `Offrez l’app ${auClub(r.club.name)}`,
      s: `${euros(official!.amount_cents)} pour la saison : un don au club, votre logo en tête de l’accueil de chaque membre`,
      act: html`<button type="button" class="btn btn-primary btn-sm" id="po-offer">J’offre l’app</button>` })] : []),
    ...openAsks.filter(a => a !== lead).map(a => fixedRow(20, { ico: 'ti-handshake', t: `${a.event} : ${a.label}`, s: a.counterpart ? `En échange : ${a.counterpart}` : `Le ${fmt(a.starts_at, { day: 'numeric', month: 'long' })}`,
      act: html`<button type="button" class="btn btn-primary btn-sm" data-guide="pa-ask" data-ask="${a.id}">Je participe</button>` })),
    ...asks.filter(a => a.mine && a.status !== 'accepte').map(a => fixedRow(60, { ico: 'ti-check', t: `${a.event} : ${a.label}`, s: 'Proposé : le club vous recontacte pour confirmer.' })),
  ];
  const slots: Slot[] = [
    { id: 'ph-lien', prio: 10, rows: el => all(el, '.card').map(c => cardRow(c, { ico: 'ti-mail' })) },   // « Le club vous invite » (D-125)
    { id: 'ph-ap', club: 1, rot: true }, { id: 'ph-donate', club: 2, label: 'Faire un don au club' }, { id: 'ph-bn', club: 3, label: 'Bénévolat' },
    { id: 'ph-official', club: 4, label: 'Partenaire officiel de l’app' },
  ];
  const pool = html`<div class="hm-pool">
    <div id="ph-lien" class="screen"></div><div id="ph-ap" class="screen"></div>
    <div id="ph-donate" class="screen">${r.club.donation_url ? html`<a class="btn btn-ghost pr-donate" href="${r.club.donation_url}" target="_blank" rel="noopener"><span class="ti ti-heart" aria-hidden="true"></span>Faire un don au club</a>` : ''}</div>
    <div id="ph-bn" class="screen"></div>
    <div id="ph-official" class="screen">${officialOn ? offerCard(official, { name: r.partner.name, logo: r.partner.logo }, r.club.name, name || null) : ''}</div></div>`;
  const todo = todoCard({ title: 'Le club vous attend', fixed: rows });

  if (page) mount(root, html`<section class="screen home">${todoPageHead('Ce que le club vous demande.')}${todo}${pool}</section>`);
  else {
    // Les chiffres du mois ; le 1er du mois (tout à 0), ceux de la saison.
    const season = Object.values(r.season).reduce((a, x) => ({ views: a.views + (x?.views ?? 0), clicks: a.clicks + (x?.clicks ?? 0) }), { views: 0, clicks: 0 });
    const useSeason = !r.month.views && !r.month.clicks && (season.views > 0 || season.clicks > 0);
    const shown = useSeason ? season : r.month;
    const when = useSeason ? 'depuis la rentrée' : 'ce mois-ci';
    mount(root, html`
      <section class="screen home">
        <header class="screen-head"><h2>${name ? `Bonjour ${name}` : 'Bonjour'}</h2>
          <div class="hm-pid">${partnerMark({ name: r.partner.name, logo: r.partner.logo }, 'pt-logo')}
            <p class="muted"><b>${r.partner.name}</b> · ${[r.partner.level ? `Partenaire ${r.partner.level}` : KIND_LABEL[r.partner.kind], r.partner.since_year ? `depuis ${r.partner.since_year}` : ''].filter(Boolean).join(' · ')}</p></div></header>
        <div id="home-first"></div>
        ${lead ? askCard(lead) : offerable ? offerCard(official, { name: r.partner.name, logo: r.partner.logo }, r.club.name, name || null) : ''}
        <div id="ph-qx"></div>
        ${todo}
        <section class="sec" data-guide="pa-kpis"><div class="sec-head"><h3 class="sec-title">Votre visibilité</h3><a class="sec-link" href="#/visibilite">Détail →</a></div>
          <div class="hm-stats">
            <a class="kpi" href="#/visibilite"><span class="eyebrow">Affichages</span><b>${shown.views.toLocaleString('fr-FR')}</b><small>${when}</small></a>
            <a class="kpi" href="#/visibilite"><span class="eyebrow">Clics</span><b>${shown.clicks.toLocaleString('fr-FR')}</b><small>${when}</small></a>
            <a class="kpi" href="#/visibilite"><span class="eyebrow">Emplacements</span><b>${live}</b><small>actifs en ce moment</small></a>
          </div></section>
        <section class="card pr-places-card">
          <p class="eyebrow">Emplacements</p>
          ${officialOn ? html`<div class="todo-row">
            <span class="todo-main"><strong>Partenaire officiel de l’app</strong><small>Saison ${official!.season} · accueil de chaque membre, page publique</small></span>
            <span class="chip chip-ok">Actif</span>
          </div>` : ''}
          <a class="todo-row" href="#/visibilite">
            <span class="todo-main"><strong>Bannière d'accueil</strong><small>${bannerOn ? `Jusqu'au ${fmt(r.partner.banner_until!, { day: 'numeric', month: 'long' })}` : pendingBanner ? 'Demande envoyée au club' : 'Aux familles du club, à tour de rôle'}</small></span>
            <span class="chip ${bannerOn ? 'chip-ok' : pendingBanner ? 'chip-warn' : 'chip-accent'}">${bannerOn ? 'Active' : pendingBanner ? 'À valider' : 'Réserver'}</span>
          </a>
          <a class="todo-row" href="#/visibilite">
            <span class="todo-main"><strong>« Match présenté par »</strong><small>${nextMatch ? `${nextMatch.team} – ${nextMatch.opponent} · ${fmt(nextMatch.starts_at, { day: 'numeric', month: 'short' })}` : 'Votre nom sur un match à domicile'}</small></span>
            <span class="chip ${nextMatch ? 'chip-ok' : 'chip-accent'}">${nextMatch ? 'À venir' : 'Réserver'}</span>
          </a>
          <a class="todo-row" href="#/publier">
            <span class="todo-main"><strong>${r.partner.member_offer ? `Offre membres : ${offerLine(r.partner.member_offer, p.member_offer_frequency, offerPerk?.stock ?? null)}` : 'Offre réservée aux membres'}</strong><small>${pendingOffer ? 'Nouvelle offre envoyée au club' : offerOut ? `${outLine(offerPerk!)} : merci ! Le club peut la remettre en jeu.` : 'Visible des licenciés et des familles'}</small></span>
            <span class="chip ${pendingOffer ? 'chip-warn' : offerOut ? 'chip-mute' : r.partner.member_offer ? 'chip-ok' : 'chip-accent'}">${pendingOffer ? 'À valider' : offerOut ? 'Épuisée' : r.partner.member_offer ? 'Active' : 'Proposer'}</span>
          </a>
          <div class="todo-row">
            <span class="todo-main"><strong>Page publique du club</strong><small>« Ils nous soutiennent »</small></span>
            <span class="chip ${r.partner.show_public ? 'chip-ok' : 'chip-mute'}">${r.partner.show_public ? 'Active' : 'Non'}</span>
          </div>
        </section>
        ${clubBlock({ title: 'Le club', href: '#/club', link: 'Le club →' })}
        ${homeFoot(null, helpFoot())}
        ${pool}
      </section>`);
  }
  startHome(root.querySelector<HTMLElement>('.home')!, { slots, max: page ? Infinity : 3, title: 'Le club vous attend', open: 0 });
  wireAsks(root, p, () => void renderPartnerHome(root, ctx, opts));
  void renderPartnerLienCard(spot(root, '#ph-lien'), ctx);   // « Le club vous invite » (D-125)
  if (page) return;
  wireOffer(root, p.report_token, official, r.partner, r.club.name, () => void renderPartnerHome(root, ctx));
  const ask = asks.find(a => !a.mine);
  void renderFirstSteps($(root, '#home-first')!, ctx, { ask: ask ? `${ask.event} : ${ask.label}` : null });   // vos premiers pas (D-091)
  void renderReferralCard(spot(root, '#ph-ap'), ctx);   // « Vous connaissez une entreprise ? » (D-096)
  void renderVolunteerLine(spot(root, '#ph-bn'), ctx, { vous: true });   // « Le club a besoin de vous » (D-097)
  void renderQrCard($(root, '#ph-qx')!, p, () => void renderPartnerHome(root, ctx));   // « Valider un avantage » (D-156)
}

/** « Valider un avantage » (D-156, maquette b6) : seulement quand son offre est un avantage en service. */
async function renderQrCard(el: HTMLElement, p: Partner, again: () => void) {
  const v = await partnerValidations(p.id, null).catch(() => null as PartnerValidations | null);
  if (!v?.perks.some(k => k.active)) return;
  mount(el, validateCard(v, '#/visibilite'));
  $(el, '[data-qx-code]')?.addEventListener('click', () => openCodeSheet(null, again));
}

/** Ses validations (D-044, D-156, maquette d1) : aujourd'hui, ce mois, ce qu'il reste ; chaque validation (l'heure, la façon,
 *  qui chez lui, le code) ; jamais le nom du membre. */
async function renderPartnerPerks(el: HTMLElement, p: Partner) {
  const v = await partnerValidations(p.id, null).catch(() => null as PartnerValidations | null);
  if (v) mount(el, partnerValidationsBlock(v) || html``);
}

/** Visibilité : les compteurs, et demander un emplacement. */
export async function renderPartnerVisibility(root: HTMLElement, ctx: Ctx): Promise<void> {
  // Visibilité vue : l'étape des premiers pas se coche (D-091).
  if (ctx.club && !ctx.club.id.startsWith('host:')) void guideMark(ctx.club.id, 'visibilite').catch(() => undefined);
  const d = await load(ctx).catch(() => null);
  if (!d) { mount(root, missing); return; }
  const { p, r, reqs } = d;
  const open = await partnerOpenMatches().catch(() => [] as OpenMatch[]);
  const places = (Object.keys(r.season) as Placement[]);
  const today = localDate(new Date());
  queueMicrotask(() => { const el = root.querySelector<HTMLElement>('#pr-perks'); if (el) void renderPartnerPerks(el, p); });
  mount(root, html`
    <section class="screen">
      <header class="screen-head"><p class="eyebrow">${r.partner.name}</p><h2>Votre visibilité</h2></header>
      <section class="card pr-kpis">
        <p class="eyebrow">Depuis le début de la saison</p>
        ${places.length ? html`<ul class="pub-list pr-places">${places.map(k => html`
          <li><span class="pub-what"><strong>${PLACEMENT_LABEL[k]}</strong><small>${views(r.season[k]!.views)} · ${clicks(r.season[k]!.clicks)}</small></span></li>`)}</ul>`
          : html`<p class="muted small">Pas encore de visibilité mesurée.</p>`}
        <p class="hint">Mesure : nombre d'affichages et de clics, sans aucun suivi des personnes.</p>
      </section>
      <div id="pr-perks"></div>
      <section class="block">
        <h3>Bannière d'accueil de l'app</h3>
        <p class="muted small">Votre logo et votre offre sur l'accueil des familles du club, à tour de rôle avec les autres partenaires.</p>
        <form class="inline-form" id="f-banner" novalidate>
          <label class="field-inline"><span class="muted small">Jusqu'au</span><input name="until" type="date" min="${today}" required></label>
          <button class="btn btn-primary btn-sm" type="submit" data-guide="pa-banner">Demander</button>
        </form>
      </section>
      <section class="block">
        <h3>« Match présenté par »</h3>
        ${open.length ? html`<ul class="list">${open.map(m => html`
          <li class="lic-pay"><span class="card-main"><strong>${m.team} – ${m.opponent}</strong><span class="muted small">${fmtAt(m.starts_at, { weekday: 'long', day: 'numeric', month: 'long' })}${m.location ? ` · ${m.location}` : ''}</span></span>
            <button type="button" class="btn btn-ghost btn-sm" data-guide="pa-match" data-match="${m.event_id}">Présenter ce match</button></li>`)}</ul>`
          : html`<p class="muted small">Aucun match à domicile disponible pour l'instant.</p>`}
      </section>
      ${reqs.length ? html`
        <section class="block">
          <h3>Vos propositions</h3>
          <ul class="ce-rows">${reqs.map(x => html`
            <li><span>${x.kind === 'banner' ? `Bannière jusqu'au ${fmt(x.banner_until!, { day: 'numeric', month: 'long' })}` : x.kind === 'match' ? '« Match présenté par »' : `Offre : ${offerLine(x.offer ?? '', x.offer_frequency)}`}</span>
              <span class="chip ${x.status === 'accepte' ? 'chip-ok' : x.status === 'refuse' ? 'chip-mute' : 'chip-warn'}">${STATUS[x.status]}</span></li>`)}</ul>
        </section>` : ''}
    </section>`);
  const again = () => void renderPartnerVisibility(root, ctx);
  const fb = $<HTMLFormElement>(root, '#f-banner')!;
  fb.addEventListener('submit', async (e) => {
    e.preventDefault();
    const until = String(new FormData(fb).get('until') ?? '');
    if (!/^\d{4}-\d{2}-\d{2}$/.test(until) || until < today) { toast('Choisissez une date à venir.', 'ko'); return; }
    try { await askPlacement({ partner_id: p.id, kind: 'banner', banner_until: until }); toast('Demande envoyée au club'); again(); }
    catch { toast('Demande impossible pour le moment.', 'ko'); }
  });
  for (const b of $$<HTMLButtonElement>(root, '[data-match]')) {
    b.addEventListener('click', async () => {
      b.disabled = true;
      try { await askPlacement({ partner_id: p.id, kind: 'match', event_id: b.dataset.match! }); toast('Demande envoyée au club'); again(); }
      catch { b.disabled = false; toast('Ce match n’est plus disponible.', 'ko'); }
    });
  }
}

/** Publier : l'offre réservée aux membres et sa fréquence (validée par le bureau). Une seule offre : la nouvelle remplace
 *  l'ancienne, et c'est elle que les membres utilisent dans Avantages (D-103). */
export async function renderPartnerPublish(root: HTMLElement, ctx: Ctx): Promise<void> {
  const d = await load(ctx).catch(() => null);
  if (!d) { mount(root, missing); return; }
  const { p, r, reqs } = d;
  const pending = reqs.find(x => x.kind === 'offer' && x.status === 'propose');
  const freq: OfferFrequency = p.member_offer_frequency ?? 'unlimited';
  const offerPerk = r.partner.member_offer ? await myOfferPerk(p).catch(() => null) : null;
  mount(root, html`
    <section class="screen">
      <header class="screen-head"><p class="eyebrow">${r.partner.name}</p><h2>Publier</h2></header>
      <section class="block">
        <h3>Offre réservée aux membres</h3>
        <p class="muted small">Visible des licenciés et des familles dans l'app du club (« Nos partenaires », bannière d'accueil) ; ils l'utilisent dans
          Avantages, devant vous. Une seule offre à la fois. Le bureau la valide avant publication.</p>
        ${r.partner.member_offer ? html`<p class="card small"><strong>En ligne :</strong> ${offerLine(r.partner.member_offer, freq, offerPerk?.stock ?? null)}${offerPerk?.exhausted_at && offerPerk.stock != null ? ` · ${outLine(offerPerk).toLowerCase()}` : ''}</p>` : ''}
        ${pending ? html`<p class="card small"><strong>À valider :</strong> ${offerLine(pending.offer ?? '', pending.offer_frequency ?? 'unlimited', pending.offer_stock)}</p>` : ''}
        <form class="form" id="f-offer" novalidate>
          <label class="field"><span>${r.partner.member_offer ? 'Nouvelle offre (remplace celle en ligne)' : 'Votre offre'}</span><input name="offer" maxlength="140" placeholder="−10 % sur la révision pour les licenciés et leurs familles"></label>
          <label class="field"><span>Combien de fois par membre</span><select name="freq">${OFFER_FREQS.map(f => html`
            <option value="${f}" ${f === freq ? 'selected' : ''}>${FREQ_LABEL[f]}</option>`)}</select></label>
          <label class="check" data-guide="pa-offer-stock"><input type="checkbox" name="limited"><span><strong>Nombre limité</strong><br>
            <span class="muted small">Premiers arrivés, premiers servis. À 0, l’offre s’arrête toute seule ; le club peut la remettre en jeu.</span></span></label>
          <div class="row-2" id="pa-stock-row" hidden><label class="field"><span>Combien en tout</span><input type="number" name="stock" inputmode="numeric" min="1" max="10000" value="5"></label><span></span></div>
          <label class="field"><span>Un mot pour le bureau (facultatif)</span><input name="message" maxlength="300"></label>
          <button class="btn btn-primary" type="submit">Envoyer au club</button>
        </form>
      </section>
      <p class="hint">Pas d'alcool ni de jeux d'argent (loi Évin), et jamais de ciblage des familles : le club affiche votre offre à tous ses membres.</p>
    </section>`);
  const f = $<HTMLFormElement>(root, '#f-offer')!;
  const limited = $<HTMLInputElement>(f, '[name=limited]')!;
  limited.addEventListener('change', () => { $(f, '#pa-stock-row')!.hidden = !limited.checked; });
  f.addEventListener('submit', async (e) => {
    e.preventDefault();
    const fd = new FormData(f);
    const offer = String(fd.get('offer') ?? '').trim();
    if (!offer) { toast('Écrivez votre offre.', 'ko'); return; }
    const fq = String(fd.get('freq') ?? '') as OfferFrequency;
    const stock = limited.checked ? Math.floor(Number(fd.get('stock'))) : null;
    if (stock !== null && !(stock >= 1 && stock <= 10000)) { toast('Combien en tout : au moins 1.', 'ko'); return; }
    try {
      await askPlacement({ partner_id: p.id, kind: 'offer', offer, offer_frequency: OFFER_FREQS.includes(fq) ? fq : 'unlimited', offer_stock: stock, message: String(fd.get('message') ?? '').trim() || null });
      toast('Offre envoyée au club'); void renderPartnerPublish(root, ctx);
    }
    catch { toast('Envoi impossible pour le moment.', 'ko'); }
  });
}

/** Club : la vitrine du club, son agenda public, le contact et le don. */
export async function renderPartnerClub(root: HTMLElement, ctx: Ctx): Promise<void> {
  const d = await load(ctx).catch(() => null);
  if (!d) { mount(root, missing); return; }
  const { r } = d;
  const agenda = r.club.public ? await publicAgenda(r.club.public).catch(() => [] as PublicEvent[]) : [];
  mount(root, html`
    <section class="screen">
      <header class="screen-head"><p class="eyebrow">Votre club</p><h2>${r.club.name}</h2></header>
      ${agenda.length ? html`
        <section class="sec"><h3 class="sec-title">Agenda du club</h3>
          <div class="pub-events">${agenda.map(ev => html`
            <article class="pub-event">
              <span class="ce-tile" aria-hidden="true"><b>${fmt(ev.starts_at, { day: 'numeric' })}</b><small>${fmt(ev.starts_at, { month: 'short' }).replace('.', '')}</small></span>
              <div class="pub-what"><small class="pub-kind">${EVENT_KIND_LABEL[ev.kind]}</small><strong>${ev.title}</strong>
                <small>${fmtAt(ev.starts_at, { weekday: 'short', day: 'numeric', month: 'short' })}${ev.location ? ` · ${ev.location}` : ''}</small></div>
            </article>`)}</div>
        </section>` : ''}
      <div class="hub-grid">
        ${r.club.public ? html`<a class="hub-tile" href="/c/${r.club.public}" target="_blank" rel="noopener"><span class="ti ti-globe" aria-hidden="true"></span><strong>Page du club</strong><small>Ce que voient les visiteurs</small></a>` : ''}
        ${r.club.donation_url ? html`<a class="hub-tile" href="${r.club.donation_url}" target="_blank" rel="noopener"><span class="ti ti-heart" aria-hidden="true"></span><strong>Faire un don</strong><small>Soutenir le club</small></a>` : ''}
      </div>
      ${r.club.email ? html`<p class="small">Une question, un projet ? <a href="mailto:${r.club.email}">${r.club.email}</a></p>` : ''}
      <div id="pa-recus"></div>
    </section>`);
  void renderMyReceiptsBlock(root.querySelector<HTMLElement>('#pa-recus')!);   // les reçus fiscaux de ses dons (D-124)
}
